/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { ProductCard, Skeleton, Typography2, breakpoints } from '@coursera/cds-core';

import Section from 'bundles/front-page/components/Section';

type Props = {
  numberOfCards: number;
  headline?: string;
  ctaText?: string;
};

const styles = {
  container: css`
    padding: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-300);
    display: grid;
    grid-template-columns: 1fr 4fr;
    gap: var(--cds-spacing-400);
    position: relative;

    ${breakpoints.down('md')} {
      display: flex;
      flex-direction: column;
      gap: var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,

  sectionTitleLoadingState: css`
    width: 75%;

    ${breakpoints.down('md')} {
      width: 40%;
    }
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  `,
  description: css`
    width: 100%;
    margin-top: var(--cds-spacing-100);
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);

    ${breakpoints.down('md')} {
      display: none;
    }
  `,
  collection: css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--cds-spacing-150);
    width: 100%;

    ${breakpoints.down('xs')} {
      /* 600 / 4 = 150 */
      grid-template-columns: repeat(4, minmax(150px, 1fr));
      width: auto;
    }

    .cds-ProductCard-grid {
      min-width: unset !important;
      border-radius: var(--cds-border-radius-100) !important;
    }

    .cds-ProductCard-content {
      padding: 0 !important;
    }

    .cds-ProductCard-gridSkeletonPreview {
      border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0 !important;
      margin: calc(var(--cds-spacing-100) * -1) calc(var(--cds-spacing-100) * -1) var(--cds-spacing-100)
        calc(var(--cds-spacing-100) * -1) !important;
      width: auto !important;
    }

    .cds-ProductCard-gridSkeletonBody {
      width: 60%;
    }

    .cds-ProductCard-gridSkeletonFooter {
      display: none !important;
    }
  `,
};

const SpotlightCollectionPlaceholder = ({ numberOfCards, headline, ctaText }: Props) => {
  return (
    <Section>
      <div css={styles.container}>
        <div css={styles.contentContainer}>
          {headline ? (
            <Typography2 component="p" variant="subtitleLarge">
              {headline}
            </Typography2>
          ) : (
            <Skeleton variant="rect" height={24} css={styles.sectionTitleLoadingState} component="div" />
          )}
          <div css={styles.description}>
            {ctaText ? (
              <Typography2 component="p" variant="bodyPrimary">
                {ctaText}
              </Typography2>
            ) : (
              <>
                <Skeleton variant="rect" height={14} width="95%" component="div" />
                <Skeleton variant="rect" height={14} width="80%" component="div" />
              </>
            )}
          </div>
        </div>
        <OverflowCarousel css={styles.collection} shouldAllowSpaceForHoverItems>
          {Array.from(Array(numberOfCards).keys()).map((value) => (
            <ProductCard key={value} variant="grid" productType="role" loading={true} />
          ))}
        </OverflowCarousel>
      </div>
    </Section>
  );
};

export default SpotlightCollectionPlaceholder;
